import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  SearchBox,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Button,
  Input,
  Field,
  TableCellLayout,
} from '@fluentui/react-components';
import {
  Eye24Regular,
  ArrowUp16Regular,
  ArrowDown16Regular,
} from '@fluentui/react-icons';
import type { EducationSchool } from '../../types';
import educationService from '../../services/educationService';
import AdvancedPagination from '../Common/AdvancedPagination';
import AdvancedFilters from '../Common/AdvancedFilters';
import ExportButton from '../Common/ExportButton';
import SchoolDetails from './SchoolDetails';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
  },
  searchArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '20px',
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: '16px',
  },
  toolbarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tableHeader: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  sortIcon: {
    marginLeft: '4px',
  },
  paginationContainer: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    zIndex: 1,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '16px',
  },
});

export const SchoolsList: React.FC = () => {
  const styles = useStyles();
  const [schools, setSchools] = useState<EducationSchool[]>([]);
  const [filteredSchools, setFilteredSchools] = useState<EducationSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDisplayName, setFilterDisplayName] = useState('');
  const [filterExternalId, setFilterExternalId] = useState('');
  const [filterPrincipalName, setFilterPrincipalName] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sorting
  const [sortField, setSortField] = useState<keyof EducationSchool | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Modal
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [schools, searchTerm, filterDisplayName, filterExternalId, filterPrincipalName, sortField, sortDirection]);

  const loadSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await educationService.getSchools(1, 999);
      setSchools(response.data);
    } catch (err: any) {
      console.error('Error loading schools:', err);
      setError(err.message || 'Erro ao carregar escolas');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...schools];

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.displayName?.toLowerCase().includes(search) ||
          s.externalId?.toLowerCase().includes(search) ||
          s.schoolNumber?.toLowerCase().includes(search)
      );
    }

    // Apply filters
    if (filterDisplayName) {
      result = result.filter((s) =>
        s.displayName?.toLowerCase().includes(filterDisplayName.toLowerCase())
      );
    }
    if (filterExternalId) {
      result = result.filter((s) =>
        s.externalId?.toLowerCase().includes(filterExternalId.toLowerCase())
      );
    }
    if (filterPrincipalName) {
      result = result.filter((s) =>
        s.principalName?.toLowerCase().includes(filterPrincipalName.toLowerCase())
      );
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredSchools(result);
    setCurrentPage(1);
  };

  const handleSort = (field: keyof EducationSchool) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleApplyFilters = () => {
    applyFiltersAndSort();
  };

  const handleClearFilters = () => {
    setFilterDisplayName('');
    setFilterExternalId('');
    setFilterPrincipalName('');
    setSearchTerm('');
  };

  const handleViewSchool = (schoolId: string) => {
    setSelectedSchoolId(schoolId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSchoolId(null);
  };

  const activeFiltersCount = [
    filterDisplayName,
    filterExternalId,
    filterPrincipalName,
  ].filter(Boolean).length;

  const exportColumns = [
    { key: 'displayName', label: 'Nome' },
    { key: 'schoolNumber', label: 'Número' },
    { key: 'externalId', label: 'ID Externo' },
    { key: 'principalName', label: 'Diretor' },
    { key: 'principalEmail', label: 'Email do Diretor' },
    { key: 'phone', label: 'Telefone' },
    { key: 'address', label: 'Endereço' },
  ];

  const totalItems = filteredSchools.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedSchools = filteredSchools.slice(startIndex, endIndex);

  const renderSortIcon = (field: keyof EducationSchool) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp16Regular className={styles.sortIcon} />
    ) : (
      <ArrowDown16Regular className={styles.sortIcon} />
    );
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Carregando escolas..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text>Erro: {error}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* SEARCH AREA - TOP */}
      <div className={styles.searchArea}>
        <AdvancedFilters
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          activeFiltersCount={activeFiltersCount}
          defaultExpanded={false}
        >
          <Field label="Nome da Escola">
            <Input
              value={filterDisplayName}
              onChange={(_, data) => setFilterDisplayName(data.value)}
              placeholder="Filtrar por nome..."
            />
          </Field>
          <Field label="ID Externo">
            <Input
              value={filterExternalId}
              onChange={(_, data) => setFilterExternalId(data.value)}
              placeholder="Filtrar por ID Externo..."
            />
          </Field>
          <Field label="Nome do Diretor">
            <Input
              value={filterPrincipalName}
              onChange={(_, data) => setFilterPrincipalName(data.value)}
              placeholder="Filtrar por diretor..."
            />
          </Field>
        </AdvancedFilters>

        <div className={styles.toolbarRow}>
          <div className={styles.toolbarLeft}>
            <SearchBox
              placeholder="Buscar por nome, número ou ID..."
              value={searchTerm}
              onChange={(_, data) => setSearchTerm(data.value)}
            />
            <Text>Total: {totalItems} escolas</Text>
          </div>
          <div className={styles.toolbarRight}>
            <ExportButton
              data={filteredSchools}
              filename="escolas"
              columns={exportColumns}
            />
          </div>
        </div>
      </div>

      {/* TABLE AREA - MIDDLE (SCROLLABLE) */}
      <div className={styles.tableContainer}>
        <Table className={styles.table} aria-label="Schools table">
          <TableHeader className={styles.tableHeader}>
            <TableRow>
              <TableHeaderCell
                className={styles.sortableHeader}
                onClick={() => handleSort('displayName')}
              >
                <TableCellLayout>
                  Nome {renderSortIcon('displayName')}
                </TableCellLayout>
              </TableHeaderCell>
              <TableHeaderCell
                className={styles.sortableHeader}
                onClick={() => handleSort('schoolNumber')}
              >
                <TableCellLayout>
                  Número {renderSortIcon('schoolNumber')}
                </TableCellLayout>
              </TableHeaderCell>
              <TableHeaderCell
                className={styles.sortableHeader}
                onClick={() => handleSort('externalId')}
              >
                <TableCellLayout>
                  ID Externo {renderSortIcon('externalId')}
                </TableCellLayout>
              </TableHeaderCell>
              <TableHeaderCell
                className={styles.sortableHeader}
                onClick={() => handleSort('principalName')}
              >
                <TableCellLayout>
                  Diretor {renderSortIcon('principalName')}
                </TableCellLayout>
              </TableHeaderCell>
              <TableHeaderCell
                className={styles.sortableHeader}
                onClick={() => handleSort('phone')}
              >
                <TableCellLayout>
                  Telefone {renderSortIcon('phone')}
                </TableCellLayout>
              </TableHeaderCell>
              <TableHeaderCell>Detalhes</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedSchools.map((school) => (
              <TableRow key={school.id}>
                <TableCell>
                  <Text weight="semibold">{school.displayName}</Text>
                </TableCell>
                <TableCell>{school.schoolNumber || '-'}</TableCell>
                <TableCell>{school.externalId || '-'}</TableCell>
                <TableCell>{school.principalName || '-'}</TableCell>
                <TableCell>{school.phone || '-'}</TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<Eye24Regular />}
                    size="small"
                    onClick={() => handleViewSchool(school.id)}
                  >
                    Ver
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* PAGINATION AREA - BOTTOM (STICKY) */}
      <div className={styles.paginationContainer}>
        <AdvancedPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <SchoolDetails
        schoolId={selectedSchoolId}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default SchoolsList;
